import React, { useState } from "react";
import styled from "styled-components";
import { Calendar, Video, ChevronDown, ChevronUp } from "lucide-react";
import VideoSection from "./VideoSection";
import { useQuery } from "@tanstack/react-query";
import { tmdbApi } from "../../services/tmdb";
import { LoadingSpinner } from "../common/LoadingSpinner";

interface Season {
  id: number;
  name: string;
  overview: string;
  episode_count: number;
  air_date: string;
  poster_path: string | null;
  season_number: number;
}

interface SeasonSectionProps {
  seasons: Season[];
  tvId: number;
}

const Section = styled.section`
  margin: var(--spacing-6) 0;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-4);
  color: var(--color-text-primary);
`;

const SeasonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
`;

const SeasonCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const SeasonHeader = styled.div`
  display: flex;
  gap: var(--spacing-4);
  padding: var(--spacing-4);
  cursor: pointer;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const PosterContainer = styled.div`
  width: 200px;
  flex-shrink: 0;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    width: 100%;
    height: 200px;
  }
`;

const Poster = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

const SeasonInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const SeasonTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: var(--spacing-2);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MetaInfo = styled.div`
  display: flex;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-3);
  color: var(--color-text-secondary);
  font-size: 0.9rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
`;

const Overview = styled.p`
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  line-height: 1.6;
`;

const SeasonContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${(props) => (props.$isOpen ? "2000px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  background: rgba(0, 0, 0, 0.3);
`;

const SeasonDetails = styled.div`
  padding: var(--spacing-4);
`;

const ToggleIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

export const SeasonSection = ({ seasons, tvId }: SeasonSectionProps) => {
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);

  const { data: seasonDetails } = useQuery({
    queryKey: ["season", tvId, selectedSeason],
    queryFn: () => selectedSeason ? tmdbApi.getSeasonDetails(tvId, selectedSeason) : null,
    enabled: selectedSeason !== null,
  });

  // Filter out specials (season 0) and sort by season number
  const regularSeasons = seasons
    .filter((season) => season.season_number > 0)
    .sort((a, b) => a.season_number - b.season_number);

  const toggleSeason = (seasonNumber: number) => {
    setSelectedSeason(selectedSeason === seasonNumber ? null : seasonNumber);
  };

  return (
    <Section>
      <Title>Saisons</Title>
      <SeasonList>
        {regularSeasons.map((season) => (
          <SeasonCard key={season.id}>
            <SeasonHeader onClick={() => toggleSeason(season.season_number)}>
              <PosterContainer>
                {season.poster_path ? (
                  <Poster
                    src={`https://image.tmdb.org/t/p/w300${season.poster_path}`}
                    alt={season.name}
                    loading="lazy"
                  />
                ) : (
                  <Poster
                    src="https://via.placeholder.com/300x450/1a1a1a/666666?text=No+Poster"
                    alt="No poster available"
                  />
                )}
              </PosterContainer>
              <SeasonInfo>
                <SeasonTitle>
                  {season.name}
                  <ToggleIcon>
                    {selectedSeason === season.season_number ? (
                      <ChevronUp size={24} />
                    ) : (
                      <ChevronDown size={24} />
                    )}
                  </ToggleIcon>
                </SeasonTitle>
                <MetaInfo>
                  {season.air_date && (
                    <MetaItem>
                      <Calendar size={16} />
                      {new Date(season.air_date).toLocaleDateString()}
                    </MetaItem>
                  )}
                  <MetaItem>
                    <Video size={16} />
                    {season.episode_count} épisodes
                  </MetaItem>
                </MetaInfo>
                <Overview>
                  {season.overview || "Aucune description disponible."}
                </Overview>
              </SeasonInfo>
            </SeasonHeader>
            <SeasonContent $isOpen={selectedSeason === season.season_number}>
              <SeasonDetails>
                {seasonDetails?.videos?.results && (
                  <VideoSection 
                    videos={seasonDetails.videos.results}
                    mediaId={tvId}
                    mediaType="tv"
                    mediaTitle={seasonDetails.name}
                  />
                )}
              </SeasonDetails>
            </SeasonContent>
          </SeasonCard>
        ))}
      </SeasonList>
    </Section>
  );
};
